import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import { ReportsService } from '../../reports.service';
import { ToastrService } from 'ngx-toastr';
import { DailogModalComponent } from '../../../shared/elements/dailog-modal/dailog-modal.component';

interface DonutPanel {
  id: string;
  header: string;
  element: any;
  legendData: Array<{ label: string; value: number; color: string; formattedValue: string; metricType: string }>;
}

@Component({
  selector: 'app-inbuilt-header-v2',
  standalone: false,
  templateUrl: './inbuilt-donut-v2.component.html',
  styleUrl: './inbuilt-donut-v2.component.css'
})
export class InbuiltHeaderV2Component implements OnInit, OnChanges, OnDestroy {

  @Input() config: any;
  @Input() elementData: any = null;
  @Input() Temp: boolean = false;
  @Output() headingResolved = new EventEmitter<string>();

  donutPanels: DonutPanel[] = [];
  isJSONeditorEnabled = false;
  isJSONeditorPopupOpen = false;
  activePanelElement: any = null;
  @ViewChild('modalEditJSON', { static: false }) modalEditJSON: any;
  private readonly destroy$ = new Subject<void>();
  private readonly responsiveColClassMap: Record<number, string> = {
    1: 'col-12 col-sm-12 col-md-1 col-lg-1 col-xl-1 col-xxl-1',
    2: 'col-12 col-sm-12 col-md-2 col-lg-2 col-xl-2 col-xxl-2',
    3: 'col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3 col-xxl-3',
    4: 'col-12 col-sm-12 col-md-4 col-lg-4 col-xl-4 col-xxl-4',
    5: 'col-12 col-sm-12 col-md-5 col-lg-5 col-xl-5 col-xxl-5',
    6: 'col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6',
    7: 'col-12 col-sm-12 col-md-7 col-lg-7 col-xl-7 col-xxl-7',
    8: 'col-12 col-sm-12 col-md-8 col-lg-8 col-xl-8 col-xxl-8',
    9: 'col-12 col-sm-12 col-md-9 col-lg-9 col-xl-9 col-xxl-9',
    10: 'col-12 col-sm-12 col-md-10 col-lg-10 col-xl-10 col-xxl-10',
    11: 'col-12 col-sm-12 col-md-11 col-lg-11 col-xl-11 col-xxl-11',
    12: 'col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12'
  };

  constructor(
    private inbuiltUtilityService: InbuiltUtilityService,
    private reportsService: ReportsService,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.inbuiltUtilityService.getJsonEditorToggleState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: boolean) => {
        this.isJSONeditorEnabled = state;
      });
    this.initializePanels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] || changes['elementData']) {
      this.initializePanels();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openJSONeditorForPanel(panelElement: any): void {
    this.activePanelElement = panelElement;
    this.isJSONeditorPopupOpen = true;
    this.modalEditJSON?.show();
  }

  deleteReport(panelElement?: any): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '400px';
    dialogConfig.hasBackdrop = true;
    dialogConfig.minHeight = '200px';
    dialogConfig.height = 'auto';
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      title: 'Delete Confirmation!',
      subTitle: 'Are you sure you want to delete this report',
      buttonName: 'Continue'
    };

    const dialogRef = this.dialog.open(DailogModalComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data !== 'cancel') {
        const target = panelElement || this.activePanelElement || this.elementData;
        const reportId = target?.element_id ?? target?.id ?? null;
        const reportName = this.config?.reportNameFromQueryParams || target?.name || this.elementData?.name || 'Report';
         const payload = {
          id: reportId,
          parent_id: this.config?.parentJSON?.id ?? null,
          parent_json: JSON.stringify(this.buildParentJsonWithElementRemoved())
        };
        this.reportsService.deleteReport(payload).subscribe({
          next: (_: any) => {
            this.toastr.success(`${reportName} has been deleted successfully`, '', {
              timeOut: 3000,
              positionClass: 'toast-top-center'
            });
             const report = this.config?.reportNameFromQueryParams ?? '';
             const pageKey = this.config?.currentPage?.key ?? '';
             const tabKey = this.config?.currentTab?.key ?? '';
             this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_page`);
             this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_tab`);
             this.inbuiltUtilityService.triggerRefreshChildData();
          },
          error: (_: any) => {
            this.toastr.error('Error deleting report', '', {
              timeOut: 3000,
              positionClass: 'toast-top-center'
            });
          }
        });
      }
    });
  }

   /** Deep-clones the parent JSON (passed down from inbuilt-report-v2 via config.parentJSON) and drops
   *  this element from the current page/tab's elements array. */
  private buildParentJsonWithElementRemoved(): any {
    const cloned = JSON.parse(JSON.stringify(this.config?.parentJSON?.parent_json || {}));
    const pageKey = this.config?.currentPage?.key ?? '';
    const tabKey = this.config?.currentTab?.key ?? '';
    const tab = cloned?.pages?.[pageKey]?.tabs?.[tabKey];
    if (tab && Array.isArray(tab.elements)) {
      const elementName = String(this.elementData?.name ?? '');
      const elementType = String(this.elementData?.type ?? '').toLowerCase();
      tab.elements = tab.elements.filter((el: any) =>
        !(String(el?.name ?? '') === elementName && String(el?.type ?? '').toLowerCase() === elementType)
      );
    }
    return cloned;
  }

  hidepopup(type: string = ''): void {
    if (type === 'edit') {
      this.isJSONeditorPopupOpen = false;
      this.modalEditJSON?.hide();
    }
  }

  onJsonEditorTextChange(ev: any): void {
    const payload = {
      report: this.config?.reportNameFromQueryParams ?? '',
      Report_Type: 'inBuilt',
      Id: this.activePanelElement?.element_id ?? null,
      element_json: ev || {},
    };
    this.reportsService.updateReportJSON(payload).subscribe({
      next: (_: any) => {
        this.toastr.success('JSON updated successfully', '', {
          timeOut: 3000,
          positionClass: 'toast-top-center'
        });
        this.modalEditJSON?.hide();
        this.isJSONeditorPopupOpen = false;
        const report = this.config?.reportNameFromQueryParams ?? '';
        const pageKey = this.config?.currentPage?.key ?? '';
        const tabKey = this.config?.currentTab?.key ?? '';
        this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_page`);
        this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_tab`);
        this.inbuiltUtilityService.triggerRefreshChildData();
      },
      error: (_: any) => {
        this.toastr.error('Error updating JSON', '', {
          timeOut: 3000,
          positionClass: 'toast-top-center'
        });
      }
    });
  }

  onPanelSeriesChange(index: number, series: Array<{ label: string; value: number; color: string; formattedValue: string; metricType: string }>): void {
    if (!this.donutPanels[index]) {
      return;
    }

    this.donutPanels[index].legendData = Array.isArray(series) ? series : [];
  }

  onPanelHeadingResolved(heading: string): void {
    if (heading) {
      this.headingResolved.emit(heading);
    }
  }

  onPanelHeadingChange(index: number, heading: string): void {
    if (!heading) return;
    if (this.donutPanels[index]) {
      this.donutPanels[index].header = heading;
    }
    this.headingResolved.emit(heading);
  }

  isDonutVisual(element: any): string {
    const normalizedType = this.normalizeType(element?.type);
    
    return normalizedType ;
  }

  onChartReady(_: number): void {}

  getPanelLayoutClass(panelElement: any): string {
    return this.resolveResponsiveColClass(panelElement?.class);
  }

  getDonutChartWidth(): number {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1366;
    const panelCount = Math.max(1, this.donutPanels?.length || 1);

    if (viewportWidth < 576) {
      return Math.max(320, Math.min(420, viewportWidth - 32));
    }

    const availablePerPanel = Math.floor((viewportWidth - 120) / panelCount);
    return Math.max(420, Math.min(620, availablePerPanel));
  }

  getDonutChartHeight(): number {
    const width = this.getDonutChartWidth();
    return Math.max(320, Math.min(460, Math.floor(width * 0.78)));
  }

  getDonutExternalLabelScale(): number {
    const width = this.getDonutChartWidth();
    if (width >= 500) {
      return 0.72;
    }
    if (width >= 420) {
      return 0.62;
    }
    return 0.5;
  }

  private initializePanels(): void {
    const elements = this.getTargetElements();
    this.donutPanels = elements.map((child: any, index: number) => ({
      id: `${index}_${String(child?.name || 'donut')}`,
      header: String(child?.header || child?.name || `Donut ${index + 1}`),
      element: child,
      legendData: []
    }));
  }

  private getTargetElements(): any[] {
    if (Array.isArray(this.elementData?.children) && this.elementData.children.length > 0) {
      return this.elementData.children;
    }

    return this.elementData ? [this.elementData] : [];
  }

  private normalizeType(type: any): string {
    return String(type ?? '').trim().toLowerCase();
  }

  private resolveResponsiveColClass(classValue: any): string {
    if (classValue === null || classValue === undefined || classValue === '') {
      return this.responsiveColClassMap[12];
    }

    if (typeof classValue === 'number' && this.responsiveColClassMap[classValue]) {
      return this.responsiveColClassMap[classValue];
    }

    const numericClass = Number(String(classValue).trim());
    if (!Number.isNaN(numericClass) && this.responsiveColClassMap[numericClass]) {
      return this.responsiveColClassMap[numericClass];
    }

    if (typeof classValue === 'string' && classValue.trim()) {
      return classValue.trim();
    }

    return this.responsiveColClassMap[12];
  }

}

